import { type ChangeEvent, type DragEvent, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { FileUpload } from '@wf/components';
import { Label } from '@wf/sw-ui-kit';

import { type FileType } from '../../constants';

import { toasterError } from '../../helpers';

import styles from './FileUploader.module.scss';

interface IFileUploaderProps {
  fileUrl?: string;
  onUpload: (file: File) => void;
  onDeleteFile?: () => void;
  errorMessage?: string;
  supportedFileTypes: FileType[];
  supportedMaxFileSizeInMB: number;
  helpText?: string;
}

function FileUploader({
  fileUrl = '',
  onUpload,
  onDeleteFile,
  errorMessage,
  supportedFileTypes,
  supportedMaxFileSizeInMB,
  helpText,
}: IFileUploaderProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);

  const supportedFileTypesRegexp = useMemo(
    () => new RegExp(supportedFileTypes.map((type) => `\\.${type}`).join('|'), 'i'),
    [supportedFileTypes]
  );

  const validateFile = (file: File) => {
    const validSizeInKB = supportedMaxFileSizeInMB * 1024 * 1024;

    return file.size <= validSizeInKB && supportedFileTypesRegexp.test(file.name);
  };

  const handleChooseFile = () => {
    inputRef?.current?.click();
  };

  const handleUpload = (file: File) => {
    const isValidFile = validateFile(file);

    if (!isValidFile) {
      toasterError({ description: 'Invalid file, check type and size' });

      return;
    }

    setIsUploading(true);
    setProgressPercent(0);

    try {
      onUpload(file);
      setFileName(file.name);
    } catch (error) {
      toasterError({ description: 'Unable to upload the file' });
      setFileName('');
    } finally {
      inputRef.current && (inputRef.current.value = '');
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { files } = e.target;

    if (files?.[0]) {
      void handleUpload(files[0]);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.files[0]) {
      void handleUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDelete = () => {
    onDeleteFile?.();
    setFileName('');
  };

  return (
    <div className={styles.uploaderWrapper}>
      <input type="file" ref={inputRef} className={styles.fileInput} onChange={handleFileChange} />
      <Label helpText={helpText}>{t('Upload File')}</Label>
      <FileUpload
        uploadProgressPercentage={isUploading ? progressPercent : undefined}
        fileName={isUploading ? t('Uploading...') : fileUrl || fileName}
        supportedMaxFileSize={`${supportedMaxFileSizeInMB}MB`}
        onDelete={!isUploading ? handleDelete : undefined}
        supportedFileTypes={supportedFileTypes}
        onChooseFile={handleChooseFile}
        className={styles.uploader}
        disabled={isUploading}
        onDrop={handleDrop}
        t={t}
      />
      {errorMessage && <div className={styles.errorMessage}>{t(errorMessage)}</div>}
    </div>
  );
}

export default FileUploader;
